import React from 'react';
import classnames from 'classnames';

/**
 * The default decorator function to decorate the ComposedCompomemt with default props.
 * This function should fit the majority of use cases, with the option of configuring the treatment
 * of how the ComposedComponent is decorated configurable by implementing a custom function like this one.
 *
 * @param {Function} ComposedComponent - the JSX component or stateless function to decorate
 * @param {Object} defaultProps - the defaultProps curried from the decorateWithDefaultProps function call.
 * @returns {Function} DecoratedComponent - the decorated component with the defaultProps applied.
 **/
const defaultDecoratorFnc = (ComposedComponent, defaultProps = {}) => {
  const DecoratedComponent = props => {
    const className = classnames(props.className, defaultProps.className);
    const combinedProps = { ...props, ...defaultProps, className };

    return <ComposedComponent {...combinedProps} />;
  };

  if (ComposedComponent.name) {
    DecoratedComponent.displayName = `Charts${ComposedComponent.name}`;
  }

  if (ComposedComponent.displayName) {
    DecoratedComponent.displayName = `Charts${ComposedComponent.displayName}`;
  }

  DecoratedComponent.propTypes = {
    ...ComposedComponent.propTypes,
  };

  DecoratedComponent.defaultProps = {
    ...ComposedComponent.defaultProps,
  };

  return DecoratedComponent;
};

/**
 * A curried Higher Order Component that decorates the composed component with
 * a set of default props. Used primarily to standardise locally scoped className to
 * a third party component without the need for global styles.
 *
 * @example
 *
 * import Select from 'react-select'
 * const StyledSelect = decorateWithDefaultProps({ className: styles.myClassName })(Select);
 *
 * @param {Object} defaultProps - the default props to pass to the composed component
 * @returns {Function} - returns a curried function to pass the component to decorate the props with, and an optional decorator function.
 */
const decorateWithDefaultProps = (defaultProps = {}) => (
  ComposedComponent,
  decoratorFnc = defaultDecoratorFnc
) => {
  return decoratorFnc(ComposedComponent, defaultProps);
};

export default decorateWithDefaultProps;
export { decorateWithDefaultProps };
